/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-nested-ternary */

import { cva, cx } from "cva";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
} from "~/landing-pages/components/CTA/cta-shared-styles";
import { heroHeadlineFontStyle } from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body } from "~/ui-library";

export interface HeroCenteredProps {
  blockIndex: number;
  appearance: "dark" | "light";
  backgroundImage?: string;
  kickerCtaDescription?: string;
  kickerCtaText?: string;
  kickerCtaLink?: string;
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
  secondaryCtaLink?: string;
  secondaryCtaText?: string;
}

const containerStyles = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "bg-gray-900",
      light: "bg-white",
    },
  },
});

const headlineStyles = cva("", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const descriptionStyles = cva(["text-body-large"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-gray-300",
      light: "text-gray-700",
    },
  },
});

const kickerContainerStyles = cva(["relative rounded-full px-3 py-1 ring-1"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-gray-400 ring-white/10 hover:ring-white/20",
      light: "text-gray-700 ring-gray-900/10 hover:ring-gray-900/20",
    },
  },
});

const kickerLinkStyles = cva(["font-semibold"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-white",
      light: "text-brand-indigo",
    },
  },
});

const appearance = "light" as HeroCenteredProps["appearance"];

export const HeroCentered = ({
  blockIndex,
  backgroundImage,
  kickerCtaDescription,
  kickerCtaText,
  kickerCtaLink,
  headline,
  description,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
}: HeroCenteredProps): JSX.Element => {
  const hasSecondaryCTA = !!(secondaryCtaLink && secondaryCtaText);
  const hasKickerCTA = !!(
    kickerCtaText &&
    kickerCtaDescription &&
    kickerCtaLink
  );

  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  const secondaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Secondary CTA", {
      ...analyticsVariables,
      linkText: secondaryCtaText,
      linkUrl: secondaryCtaLink,
    });
  const onKickerLinkClick = () =>
    analyticsEnhancedTrack("Clicked Kicker CTA", {
      ...analyticsVariables,
      linkDescription: kickerCtaDescription,
      linkText: kickerCtaText,
      linkUrl: kickerCtaLink,
    });

  return (
    <div className={containerStyles({ appearance })}>
      <div className="relative isolate overflow-hidden pt-14">
        {backgroundImage ? (
          <img
            alt=""
            src={backgroundImage}
            className="absolute inset-0 -z-10 size-full object-cover"
          />
        ) : null}
        <div className="w-full">
          {hasKickerCTA && (
            <div className="mb-8 hidden sm:flex sm:justify-center">
              <Body as="div" className={kickerContainerStyles({ appearance })}>
                {kickerCtaDescription}{" "}
                <CTALink
                  href={kickerCtaLink}
                  onClick={onKickerLinkClick}
                  className={kickerLinkStyles({ appearance })}
                >
                  {kickerCtaText}
                </CTALink>
              </Body>
            </div>
          )}
          <div className="text-center">
            <h1
              className={cx(
                heroHeadlineFontStyle(),
                headlineStyles({ appearance })
              )}
            >
              {headline}
            </h1>
            <div className={descriptionStyles({ appearance })}>
              <BuildHTML
                html={description}
                tagToClassNameMap={tagToClassNameMap}
              />
            </div>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <CTALink
                href={primaryCtaLink}
                onClick={primaryButtonClick}
                className={primaryButtonStyle({ appearance })}
              >
                {primaryCtaText}
              </CTALink>
              {hasSecondaryCTA && (
                <CTALink
                  href={secondaryCtaLink}
                  onClick={secondaryButtonClick}
                  className={secondaryButtonStyle({ appearance })}
                >
                  {secondaryCtaText}
                </CTALink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
